import { useCallback } from 'react';
import { emptyObject } from '@fabric-ui/utils';
import { useQueries, useQuery, useQueryClient, type QueryObserverOptions } from '@tanstack/react-query';
import { QueryObject } from 'src/views/CustomDashboards/types';
import { useAvContext } from '../../context/AvContextProvider';
import { FeatureFlags } from '../../types';
import { type Filter } from '../../types/filter.types';
import { cleanEmptyFilters } from '../../utils/filterUtils';
import { AliasResolution, FeatureFlagsOverrides } from '../../views/CustomDashboards/types/QueryObject.types';
import { QueryObjectTags } from '../queryObjectProps.types';
import useHandleError from '../useHandleError';
import {
  getAsyncPollDwQueryQueryOptions,
  getAsyncQueryStatus,
  getQueryObjectAsyncQueryOptions,
  selectDataFromResults,
} from './useQueryObjectAsyncWithCachedResults.utils';

export type QueryObjectProps = {
  queryObject: QueryObject | QueryObject[];
  origin?: string;
  showTotals?: boolean;
  totalRowCount?: boolean;
  options?: Partial<QueryObserverOptions>;
  contextFilter?: Filter;
  searchQuery?: string;
  fileFormat?: string;
  aliasResolution?: AliasResolution;
  tags?: QueryObjectTags;
  featureFlagsOverrides?: FeatureFlagsOverrides;
  showStaleData?: boolean;
};

export type QueriesObjectProps = {
  queryObject: QueryObject[];
  origin?: string;
  onSuccess?: (d: any[], index: number) => any[];
  onError?: (error) => void;
  muteErrors?: boolean;
  showTotals?: boolean;
  totalRowCount?: boolean;
  options?: Partial<QueryObserverOptions>;
  contextFilter?: Filter;
  searchQuery?: string;
  fileFormat?: string;
  aliasResolution?: AliasResolution;
  tags?: QueryObjectTags;
  isCacheEnabled?: boolean;
  showStaleData?: boolean;
};

export const useQueryObjectAsyncWithCachedResult = ({
  queryObject: _queryObject,
  origin = 'INTERACTIVE_QUERY',
  showTotals = false,
  totalRowCount = false,
  options = {},
  contextFilter,
  searchQuery,
  fileFormat = 'JSON_FORMAT',
  aliasResolution,
  tags,
  featureFlagsOverrides,
  showStaleData = false,
}: QueryObjectProps) => {
  const { featureFlags, api } = useAvContext();
  const handleError = useHandleError(featureFlags[FeatureFlags.ShowServerErrorInToast]);
  const [queryObject] = Array.isArray(_queryObject) ? _queryObject : [_queryObject];
  const queryClient = useQueryClient();
  const enabled =
    Boolean(options.enabled || options.enabled === undefined) &&
    Boolean(queryObject?.select?.dims?.length || queryObject?.select?.metrics?.length);

  const queryObjectAsyncQuery = useQuery({
    ...getQueryObjectAsyncQueryOptions({
      api,
      query: queryObject,
      origin,
      showTotals,
      totalRowCount,
      fileFormat,
      searchQuery,
      aliasResolution,
      contextFilter: cleanEmptyFilters(contextFilter),
      tags,
      featureFlagsOverrides,
      options,
    }),
    enabled,
  });

  const enablePolling = Boolean(queryObjectAsyncQuery.data?.shouldPollWithQueryId && queryObjectAsyncQuery.data.queryId && enabled);

  const asyncPollQuery = useQuery({
    ...getAsyncPollDwQueryQueryOptions({
      api,
      queryId: queryObjectAsyncQuery.data?.queryId,
      onError: handleError,
      queryClient,
    }),
    enabled: enablePolling,
  });

  const asyncQueryStatus = getAsyncQueryStatus(asyncPollQuery, queryObjectAsyncQuery, showStaleData);
  const data = selectDataFromResults(asyncPollQuery, queryObjectAsyncQuery, showStaleData);

  return {
    isLoading: queryObjectAsyncQuery.isLoading,
    isRefetching: asyncPollQuery.isLoading || asyncPollQuery.data?.asyncResult === 'ASYNC_RUNNING',
    data: data?.rows,
    totals: (Array.isArray(data?.totals) ? data.totals : data?.totals?.data) ?? undefined,
    totalRowCount: data?.totalRowCount ?? undefined,
    asyncResult: asyncPollQuery?.data?.asyncResult || undefined,
    isDebouncing: false,
    responseCalculatedAt: queryObjectAsyncQuery.data?.responseCalculatedAt ?? undefined,
    asyncQueryStatus,
    errors: [
      ...(Array.isArray(queryObjectAsyncQuery.error) ? queryObjectAsyncQuery.error : [queryObjectAsyncQuery.error]),
      ...(Array.isArray(asyncPollQuery.error) ? asyncPollQuery.error : [asyncPollQuery.error]),
    ].filter(Boolean),
  };
};

export const useQueriesObjectAsyncWithCachedResult = ({
  queryObject: _queryObject,
  origin = 'INTERACTIVE_QUERY',
  onSuccess = d => d,
  onError = undefined,
  muteErrors = undefined,
  showTotals = false,
  totalRowCount = false,
  options = emptyObject,
  contextFilter,
  searchQuery,
  fileFormat = 'JSON_FORMAT',
  aliasResolution,
  tags,
  isCacheEnabled = true,
  showStaleData = false,
}: QueriesObjectProps) => {
  const { featureFlags, api } = useAvContext();
  const handleError = useHandleError(featureFlags[FeatureFlags.ShowServerErrorInToast]);
  const queryClient = useQueryClient();

  const queryObject = Array.isArray(_queryObject) ? _queryObject : [_queryObject];
  const enabled =
    Boolean(options.enabled || options.enabled === undefined) &&
    queryObject.some(qo => qo?.select?.dims?.length || qo?.select?.metrics?.length);

  const queryObjectAsyncQueries = useQueries({
    queries: queryObject.map<ReturnType<typeof getQueryObjectAsyncQueryOptions>>(query => ({
      ...getQueryObjectAsyncQueryOptions({
        api,
        query,
        origin,
        showTotals,
        totalRowCount,
        fileFormat,
        searchQuery,
        aliasResolution,
        contextFilter: cleanEmptyFilters(contextFilter),
        tags,
        isCacheEnabled,
      }),
      enabled,
    })),
  });

  const asyncPollQueries = useQueries({
    queries: queryObjectAsyncQueries.map(({ data }) => ({
      ...getAsyncPollDwQueryQueryOptions({
        api,
        queryId: data?.queryId,
        onError: onError || handleError,
        queryClient,
        muteErrors,
      }),
      enabled: enabled && !!(data?.shouldPollWithQueryId && data?.queryId),
    })),
  });

  const asyncQueryStatus = queryObjectAsyncQueries.map((queryObjectAsyncQuery, index) =>
    getAsyncQueryStatus(asyncPollQueries[index], queryObjectAsyncQuery, showStaleData)
  );

  const results: any[] = queryObjectAsyncQueries.map((queryObjectAsyncQuery, index) =>
    selectDataFromResults(asyncPollQueries[index], queryObjectAsyncQuery, showStaleData)
  );
  const newResults = (results || []).map((result, index) => ({
    ...result,
    data: result?.rows?.length ? onSuccess(result?.rows, index) : result?.rows,
  }));

  const isLoading = queryObjectAsyncQueries.some(query => query.isLoading) || asyncPollQueries.some(query => query.isLoading);
  const isRefetching = asyncPollQueries.some(query => query.isLoading || query.data?.asyncResult === 'ASYNC_RUNNING');
  const errors = [
    ...queryObjectAsyncQueries.flatMap(query => (Array.isArray(query.error) ? query.error : [query.error])),
    ...asyncPollQueries.flatMap(query => (Array.isArray(query.error) ? query.error : [query.error])),
  ].filter(Boolean);

  const refetch = useCallback(() => Promise.all(asyncPollQueries.map(r => r.refetch())), [newResults]);
  return {
    asyncResult: !newResults.length
      ? undefined
      : newResults.length === 1
        ? newResults[0]?.asyncResult
        : newResults.map(data => data?.asyncResult),
    asyncQueryStatus,
    totalRowCount: !newResults.length ? undefined : newResults[0]?.totalRowCount,
    totals: !newResults.length ? undefined : newResults.length === 1 ? newResults[0]?.totals : newResults.map(data => data?.totals),
    data:
      !newResults.length || (queryObject.length > 1 && newResults.some(data => !(data?.data || data)?.length))
        ? undefined
        : newResults.length === 1
          ? newResults[0]?.data
          : newResults.map(({ data }) => data?.data || data),
    isLoading,
    errors: errors.map(result => result.error).filter(Boolean) || [],
    refetch,
    isRefetching,
  };
};
